import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { useSearchParams } from 'react-router-dom';
import { WarningIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { ApiProblem } from '@/infrastructure/api/v1/problem';
import { AuthShell, type AuthStatus } from '@/pages/landing/AuthShell';
import {
  approveMcpAuthorization,
  describeMcpAuthorization,
  type McpAuthorizationDecision,
  type McpAuthorizationRequest,
} from '@/modules/connectClaude/mcpConnections';

const EXPIRED = 'This sign-in request has expired or is not valid. Go back to Claude and connect again.';
const UNREGISTERED = 'This app is no longer registered with Lureo. Go back to Claude and connect again.';
const SIGNED_OUT = 'Your Lureo session has ended. Sign in again, then connect from Claude again.';
const FAILED = 'Something went wrong. Try again, or go back to Claude and connect again.';
const UNAVAILABLE = "Connecting apps to Lureo isn't available on your account yet.";

/** The message for a failed describe or decision, by the problem's status and code. */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiProblem)) return FAILED;
  if (error.code === 'authorization_request_invalid') return EXPIRED;
  if (error.code === 'mcp_unavailable') return UNAVAILABLE;
  if (error.status === 404) return UNREGISTERED;
  if (error.status === 401) return SIGNED_OUT;
  return FAILED;
}

/**
 * Where an MCP client's sign-in (Claude's "Connect") sends the browser: asks the signed-in user to allow or deny the
 * client, then sends the browser on to where the client waits.
 */
export default function ConnectApprove() {
  const [params] = useSearchParams();
  const request = params.get('request');
  const [described, setDescribed] = useState<McpAuthorizationRequest | null>(null);
  const [error, setError] = useState<string | null>(request ? null : EXPIRED);
  const [deciding, setDeciding] = useState<McpAuthorizationDecision | null>(null);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    describeMcpAuthorization(request)
      .then(result => {
        if (!cancelled) setDescribed(result);
      })
      .catch(failure => {
        if (!cancelled) setError(messageFor(failure));
      });
    return () => {
      cancelled = true;
    };
  }, [request]);

  const decide = async (decision: McpAuthorizationDecision) => {
    if (!request) return;
    setDeciding(decision);
    setError(null);
    try {
      window.location.href = await approveMcpAuthorization(request, decision);
    } catch (failure) {
      setError(messageFor(failure));
      setDeciding(null);
    }
  };

  const status: AuthStatus | null = error ? { message: error, tone: 'error' } : null;

  return (
    <AuthShell title="Connect to Lureo" status={status} showOAuth={false} onError={setError}>
      {!described && !error && (
        <div className="flex justify-center py-4 text-lureo-ink-subtle">
          <LoadingSpinner size="md" className="h-6 w-6" />
        </div>
      )}
      {described && (
        <div className="flex flex-col gap-4">
          <p className="text-lureo-14 text-lureo-ink leading-5">
            Allow <span className="font-semibold">{described.clientName}</span> ({described.clientHost}) to read and edit your
            Lureo scenes?
          </p>
          {!described.clientVerified && (
            <div className="flex items-start gap-2 rounded-lureo-radius border-lureo-border-width border-lureo-warning/50 bg-lureo-warning/5 p-3 text-lureo-13 leading-snug text-lureo-ink">
              <WarningIcon className="mt-px h-4 w-4 shrink-0 text-lureo-warning-ink" />
              <span>Lureo could not verify this app. Only approve it if you started this connection yourself.</span>
            </div>
          )}
          <div className="flex gap-3">
            <LureoButton
              variant="outline-3"
              onClick={() => decide('deny')}
              disabled={deciding !== null}
              className="h-10 flex-1 rounded-lureo-radius-button-pill"
            >
              Deny
            </LureoButton>
            <LureoButton
              variant="secondary"
              onClick={() => decide('approve')}
              disabled={deciding !== null}
              className="h-10 flex-1 rounded-lureo-radius-button-pill"
            >
              {deciding === 'approve' && <LoadingSpinner size="sm" tone="on-cta" />}
              Approve
            </LureoButton>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
